/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import { ScrollView, StyleProp, View, ViewStyle } from "react-native";
import { useQuery } from "@apollo/client";

import { useSelector } from "react-redux";

import { Text, UpduoButton, ButtonType, LoadingDots } from "UpduoLibrary";
import { RootState } from "store/types";
import { GetQuotaReportQuery } from "graphql/internal/generated";
import { GET_QUOTA_REPORT } from "graphql/internal/Queries";

import {
  formattedReadableDateStringMonthDay,
  isNotNil,
} from "config/Utilities";
import {
  skaleModalTextGray,
  skaleRed,
  upduoPrimaryBlack,
  upduoPrimaryBlue,
  upduoBlueBackground,
} from "config/UpduoColors";
import { dashboardStyles } from "./shared_components";

import * as UpduoAnalytics from "config/UpduoAnalytics";
import { useHistory } from "react-router";

interface SummitInfo {
  title: string;
  subtitle: string;
  completed: number;
  total: number;
  unit: string;
  disabled?: boolean;
}

function InfoMeter({ info }: { info: SummitInfo }) {
  const percentageComplete = (info.completed / info.total) * 100;
  return (
    <View
      style={{ marginBottom: 22, opacity: info.disabled ? 0.5 : undefined }}
    >
      <View
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          marginBottom: 2,
        }}
      >
        <Text
          isHeading={true}
          style={{
            fontSize: 14,
            color: upduoPrimaryBlack,
          }}
        >
          {info.title}
        </Text>
        <Text
          isHeading={true}
          style={{
            fontSize: 14,
            color: upduoPrimaryBlack,
          }}
        >
          {info.completed}
          {"/"}
          {info.total}
        </Text>
      </View>
      <View
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <Text
          style={{
            fontSize: 12,
            width: "85%",
            color: upduoPrimaryBlack,
          }}
          numberOfLines={1}
        >
          {info.subtitle}
        </Text>
        <Text
          style={{
            color: upduoPrimaryBlack,
            fontSize: 12,
          }}
        >
          {info.unit}
        </Text>
      </View>
      <View
        style={{
          width: "100%",
          height: 6,
          backgroundColor: upduoBlueBackground,
          borderRadius: 5,
          overflow: "hidden",
        }}
      >
        <View
          style={{
            backgroundColor: upduoPrimaryBlue,
            width: `${percentageComplete}%`,
            height: "100%",
          }}
        />
      </View>
    </View>
  );
}

export default function SessionCompletionWidget({
  userId,
  style,
  hideConnectButton,
}: {
  userId?: string;
  hideConnectButton?: boolean;
  style?: StyleProp<ViewStyle>;
}) {
  const history = useHistory();

  const selfObject = useSelector((state: RootState) => state.selfObject);

  const { loading, error, data } = useQuery<GetQuotaReportQuery>(
    GET_QUOTA_REPORT,
    {
      fetchPolicy: "network-only",
      variables: {
        userId,
      },
      onError: (error) => {
        UpduoAnalytics.logEvent(
          "app-sessioncompletewidget-loadquotacompletioncount-error",
          {
            error,
          }
        );
      },
    }
  );

  const report = data?.quotaReport;

  const meters: SummitInfo[] = [];

  if (isNotNil(report)) {
    if (
      selfObject.organizationActiveQuota &&
      isNotNil(report.quotaCompletedSessions)
    ) {
      const startDate = new Date(selfObject.organizationActiveQuota.startDate);
      const endDate = new Date(selfObject.organizationActiveQuota.endDate);
      meters.push({
        title: "Quota Completion",
        subtitle: `${formattedReadableDateStringMonthDay(
          startDate
        )} - ${formattedReadableDateStringMonthDay(endDate)}`,
        completed: report.quotaCompletedSessions,
        total: selfObject.organizationActiveQuota?.requiredSessionCount,
        unit: "user sessions",
      });
    } else {
      const allSummits = [
        ...(report.activeSummitCompletion
          ? [report.activeSummitCompletion]
          : []),
        ...report.pastSummitCompletion,
      ];
      const totalSkills = allSummits
        .map((summit) => summit.totalActiveSkills)
        .reduce((a, b) => a + b, 0);
      if (totalSkills > 0) {
        meters.push({
          title: "Monthly Goal",
          subtitle: "Current month",
          completed: report.monthlyCompletedSessions || 0,
          total: totalSkills,
          unit: "sessions",
        });
      }
    }

    const activeSummit = report.activeSummitCompletion;
    if (activeSummit) {
      meters.push({
        title: "Current Event's Topic Completion",
        subtitle: activeSummit.summit.name,
        completed: activeSummit.completedSkills,
        total: activeSummit.totalActiveSkills,
        unit: "lessons",
      });
    }

    for (const summit of report.pastSummitCompletion) {
      meters.push({
        title: "Past Event Completion",
        subtitle: summit.summit.name,
        completed: summit.completedSkills,
        total: summit.totalActiveSkills,
        unit: "lessons",
        disabled: true,
      });
    }
  }

  const _handleConnect = () => {
    const activeSummit = report?.activeSummitCompletion;
    if (!activeSummit) return;

    history.push(`/event/${activeSummit.summit.id}`);
  };

  const isLoading = loading;

  return (
    <View style={[{ padding: 20 }, dashboardStyles.dashboardWidget, style]}>
      {isLoading ? (
        <View
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <LoadingDots />
        </View>
      ) : (
        <>
          <View
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              paddingBottom: 20,
            }}
          >
            <Text style={dashboardStyles.widgetTitle}>
              {"Session Completion"}
            </Text>
            {hideConnectButton ? null : (
              <UpduoButton
                type={ButtonType.PRIMARY}
                text={"Connect"}
                onPress={() => {
                  _handleConnect();
                }}
                disabled={!report?.activeSummitCompletion}
              />
            )}
          </View>

          {error || !report ? (
            <Text
              style={{
                color: skaleRed,
                textAlign: "center",
              }}
            >
              {"Error loading quota status"}
            </Text>
          ) : meters.length > 0 ? (
            <ScrollView style={{ paddingRight: 15 }}>
              {meters.map((x, i) => (
                <InfoMeter info={x} key={i} />
              ))}
            </ScrollView>
          ) : (
            <Text
              style={{
                fontWeight: "bold",
                color: skaleModalTextGray,
                textAlign: "center",
              }}
            >
              {"No goals have been set"}
            </Text>
          )}
        </>
      )}
    </View>
  );
}
